// ----------------------------------------------------------------------------
// Copyright (c) Ben Coleman, 2021
// Licensed under the MIT License.
//
// Drop in MSAL.js 2.x service wrapper & helper for SPAs
//   v2.1.0 - Ben Coleman 2019
//   Updated 2021 - Switched to @azure/msal-browser
// ----------------------------------------------------------------------------

import * as msal from '@azure/msal-browser'
import clearCache from '../functions/clearCache'
// MSAL object used for signing in users with MS identity platform
let msalApp

export default {
  async handleRedirectResponse() {
    try {
      const response = await msalApp.handleRedirectPromise()
      if (response) {
        console.log('Login successful:', response)
        return true
      }
    } catch (error) {
      console.error('Error handling redirect response:', error)
      return false
    }
  },

  async configure(clientId) {
    // Can only call configure once
    if (msalApp) {
      return
    }

    // Can't configure if clientId blank/null/undefined
    if (!clientId) {
      return
    }
    const config = {
      auth: {
        clientId: process.env.VUE_APP_ROOT_AZURE_CLIENT_ID,
        redirectUri: window.location.origin,
        authority: `https://login.microsoftonline.com/${process.env.VUE_APP_ROOT_AZURE_LOCATARIO_ID}`,
      },
      cache: {
        cacheLocation: 'localStorage',
        storeAuthStateInCookie: true,
      },
      system: {
        loggerOptions: {
          loggerCallback: (level, message, containsPii) => {
            if (containsPii) {
              return
            }
            switch (level) {
              case msal.LogLevel.Error:
                return
              case msal.LogLevel.Info:
                return
              case msal.LogLevel.Verbose:
                return
              case msal.LogLevel.Warning:
                return
              default:
                return
            }
          },
        },
      },
    }

    // Create our shared/static MSAL app object
    msalApp = new msal.PublicClientApplication(config)
  },

  //
  // Return the configured client id
  //
  clientId() {
    if (!msalApp) {
      return null
    }

    return msalApp.config.auth.clientId
  },

  async login(
    scopes = [
      `api://${process.env.VUE_APP_ROOT_AZURE_CLIENT_ID}/sgis.operacao.acesso`,
    ],
  ) {
    if (!msalApp) {
      return
    }

    //Login por Popup
    // await msalApp.loginPopup({
    //   scopes,
    //   prompt: 'select_account',
    // })
    //Login por Redirect
    await msalApp.loginRedirect({
      scopes: scopes,
      prompt: 'select_account',
    })
    await msalApp
      .handleRedirectPromise()
      .then((response) => {
        if (response) {
          console.log('Login successful:', response)
        }
      })
      .catch((error) => {
        console.error('Error during login redirect:', error)
      })
  },

  //
  // Logout any stored user
  //
  logout() {
    try {
      if (!msalApp) {
        return
      }

      msalApp.logoutRedirect({
        postLogoutRedirectUri: process.env.VUE_APP_ROOT_AZURE_URL_REDIRECT, // URL para redirecionar após o logout
      })
    } catch (e) {
      clearCache()
    }
  },

  //
  // Call to get user, probably cached and stored locally by MSAL
  //
  user() {
    if (!msalApp) {
      return null
    }

    const currentAccounts = msalApp.getAllAccounts()
    if (!currentAccounts || currentAccounts.length === 0) {
      // No user signed in
      return null
    } else if (currentAccounts.length > 1) {
      return currentAccounts[0]
    } else {
      return currentAccounts[0]
    }
  },

  //
  // Call through to acquireTokenSilent or acquireTokenPopup
  //
  async acquireToken(
    scopes = [
      `api://${process.env.VUE_APP_ROOT_AZURE_CLIENT_ID}/sgis.operacao.acesso`,
    ],
  ) {
    if (!msalApp) {
      return null
    }

    // Set scopes for token request
    const accessTokenRequest = {
      scopes,
      account: this.user(),
    }

    let tokenResp
    try {
      // 1. Try to acquire token silently
      tokenResp = await msalApp.acquireTokenSilent(accessTokenRequest)
    } catch (err) {
      // 2. Silent process might have failed so try via popup
      tokenResp = await msalApp.acquireTokenPopup(accessTokenRequest)
    }
    if (!tokenResp.accessToken) {
      throw new Error("### accessToken not found in response, that's bad")
    }

    return tokenResp.accessToken
  },

  clearLocal() {
    if (msalApp) {
      for (let entry of Object.entries(localStorage)) {
        let key = entry[0]
        if (key.includes('login.windows')) {
          localStorage.removeItem(key)
        }
      }
    }
  },

  isConfigured() {
    return msalApp != null
  },
}
